import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { isDifferentDate } from '@shared/helpers/date-helper';
import { LoggerCodes, LoggerService } from '@shared/modules/logger/logger';
import {
  ClearRequestPayload,
  ClearResponse,
  ClearService,
  LegacyClearResponse,
} from '@shared/services/clear/clear.service';
import { StopPendingEffectsService } from '@src/app/core/services/stop-pending-effects.service';
import { selectIsReviewPageReachedAndEditMode } from '@store/selectors/transaction-state/transaction-state.selectors';
import { catchError, switchMap, takeUntil, withLatestFrom } from 'rxjs';

import {
  ClearEnd,
  ClearError,
  ClearStart,
  DecedentDODUpdate,
  Next,
  TrackProcessError,
  TrackProcessSideStep,
} from '../../actions';
import { DecedentValidationStart } from '../../actions/decedent-security/decedent-security.actions';
import * as fromRootStore from '../../index';
import {
  selectDecedentDOD,
  selectDecedentInfoState,
} from '../../selectors/decedent-info/decedent-info.selectors';
import { selectInheritanceDecedentSecurityToggle } from '../../selectors/feature/feature.selectors';

@Injectable()
export class ClearEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<fromRootStore.State>,
    private clearService: ClearService,
    private loggerService: LoggerService,
    private stopPendingEffectsService: StopPendingEffectsService,
  ) {}

  clearStart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClearStart),
      withLatestFrom(this.store$.pipe(select(selectDecedentInfoState))),
      switchMap(([, decedentInfoState]) => {
        return this.clearService
          .callExternalClientSearch({
            firstName: decedentInfoState.firstName,
            lastName: decedentInfoState.lastName,
            decedentId_private: decedentInfoState.decedentId_private,
            decedentPoid: decedentInfoState.decedentPoid,
            dateOfBirth: decedentInfoState.dateOfBirth,
          } as ClearRequestPayload)
          .pipe(
            switchMap((clearResponse: ClearResponse | LegacyClearResponse) => {
              let clearMessage;

              if (clearResponse.CNMKongToggle) {
                if ((clearResponse as ClearResponse).deceased) {
                  clearMessage = 'clear-verified_deceased';
                  this.loggerService.info(
                    {
                      message:
                        'Kong External client search returned deceased true, skipping document upload page',
                    },
                    LoggerCodes.DECEASED_TRUE,
                  );
                } else {
                  clearMessage = 'clear-verified_living';
                  this.loggerService.info(
                    {
                      message:
                        'Kong External client search returned deceased false, routing to document upload page',
                    },
                    LoggerCodes.DECEASED_FALSE,
                  );
                }

                return [
                  TrackProcessSideStep({
                    processType: 'coo-inheritance-flow',
                    processStep: `coo-inheritance-doc_upload`,
                    processSideStep: clearMessage,
                  }),
                  ClearEnd({ payload: clearResponse as ClearResponse }),
                ];
              } else {
                if ((clearResponse as LegacyClearResponse).isDeceased) {
                  clearMessage = 'clear-verified_deceased';
                  this.loggerService.info(
                    {
                      message:
                        'Legacy External client search returned deceased true, skipping document upload page',
                    },
                    LoggerCodes.DECEASED_TRUE,
                  );
                } else {
                  clearMessage = 'clear-verified_living';
                  this.loggerService.info(
                    {
                      message:
                        'Legacy External client search returned deceased false, routing to document upload page',
                    },
                    LoggerCodes.DECEASED_FALSE,
                  );
                }
                return [
                  TrackProcessSideStep({
                    processType: 'coo-inheritance-flow',
                    processStep: `coo-inheritance-doc_upload`,
                    processSideStep: clearMessage,
                  }),
                  ClearEnd({ payload: clearResponse as LegacyClearResponse }),
                ];
              }
            }),
            catchError((err) => {
              this.loggerService.info(
                {
                  message: `External client search unsuccessful/unavailable: ${err.message}, routing to document upload page`,
                },
                LoggerCodes.DECEASED_NOT_FOUND,
              );
              return [
                TrackProcessError({
                  processStep: `coo-inheritance-doc_upload`,
                  errorCode: 'clear-unverified',
                }),
                ClearError(),
              ];
            }),
          );
      }),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  clearEnd$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClearEnd),
      withLatestFrom(
        this.store$.pipe(select(selectIsReviewPageReachedAndEditMode)),
        this.store$.pipe(select(selectDecedentDOD)),
        this.store$.pipe(select(selectInheritanceDecedentSecurityToggle)),
      ),
      switchMap(
        ([{ payload }, isReviewPageReachedAndEditMode, decedentDOD, isDecedentSecurityEnabled]) => {
          if (
            isReviewPageReachedAndEditMode &&
            payload.deathDate &&
            isDifferentDate(decedentDOD, payload.deathDate)
          ) {
            this.loggerService.info(
              {
                eventId: 'multi-inheritance-clear-dod-update',
                message: 'Multi-Inheritance clear DOD update after edit from final review page',
              },
              LoggerCodes.GENERAL_SUCCESS,
            );
            return isDecedentSecurityEnabled
              ? [
                  DecedentDODUpdate({ payload: { deathDate: payload.deathDate } }),
                  DecedentValidationStart(),
                ]
              : [DecedentDODUpdate({ payload: { deathDate: payload.deathDate } }), Next()];
          }
          return isDecedentSecurityEnabled ? [DecedentValidationStart()] : [Next()];
        },
      ),
      takeUntil(this.stopPendingEffectsService.stopObservable$),
    ),
  );

  clearError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClearError),
      withLatestFrom(this.store$.pipe(select(selectInheritanceDecedentSecurityToggle))),
      switchMap(([, isDecedentSecurityEnabled]) => {
        return isDecedentSecurityEnabled ? [DecedentValidationStart()] : [Next()];
      }),
    ),
  );
}
